import { Link } from "react-router-dom";
import useSWR from "swr";
import { ArrowLeft, Printer } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { useSavedPairs, pairKey } from "@/lib/savedPairs";
import { useShortlist } from "@/context/ShortlistContext";
import { LogoMark } from "@/components/Logo";
import { finishBg } from "@/components/FinishDots";
import { useSheetZoom } from "@/lib/useSheetZoom";

const Swatch = ({ label, item, extra }) => (
  <div className="min-w-0">
    <div className="aspect-square bg-[#eee8da] overflow-hidden border border-[#d9cfb4]">
      {item && <img src={imgUrl(item.images?.[0])} alt={item.name} className="w-full h-full object-cover" />}
    </div>
    <p className="mt-1.5 font-mono-j text-[8px] tracking-[0.2em] uppercase text-[#9a7b26]">{label}</p>
    <p className="font-display text-[14px] leading-tight text-[#14130f]">{item?.name || "—"}</p>
    <p className="text-[9px] text-[#5d5a52]">{[item?.supplier, item?.code].filter(Boolean).join(" · ")}</p>
    {extra}
  </div>
);

export default function PairPrintPage() {
  const { pairs } = useSavedPairs();
  const sheetZoom = useSheetZoom();
  const { client, project } = useShortlist();
  const { data: b } = useSWR("/items?category=board-products", fetcher);
  const { data: s } = useSWR("/items?category=stone-options", fetcher);
  const { data: h } = useSWR("/items?category=hardware", fetcher);
  const all = [b, s, h].flatMap(asList);
  const get = (id) => all.find((i) => i.id === id);

  return (
    <div className="print-root min-h-screen bg-[#0D0D11]">
      <div className="no-print sticky top-0 z-40 bg-[#0D0D11]/90 backdrop-blur-xl border-b border-[#D4AF37]/15">
        <div className="mx-auto max-w-[900px] px-5 py-4 flex items-center justify-between gap-3">
          <Link to="/pairing" data-testid="pair-print-back" className="inline-flex items-center gap-2 text-sm text-[#A09E97] hover:text-[#E5C158]"><ArrowLeft className="w-4 h-4" /> Pairing studio</Link>
          <button onClick={() => window.print()} data-testid="pair-print-now"
            className="h-11 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] transition-colors">
            <Printer className="w-4 h-4" /> Print<span className="hidden sm:inline"> / Save PDF</span>
          </button>
        </div>
      </div>
      <div className="sheets py-6 sm:py-10" style={sheetZoom}>
        <section className="sheet" data-testid="pair-print-sheet">
          <header className="flex items-end justify-between border-b border-[#9a7b26] pb-3 mb-5">
            <div>
              <p className="font-mono-j text-[9px] tracking-[0.3em] uppercase text-[#9a7b26]">Sterling Joinery · Pairing studio</p>
              <h1 className="font-display text-[36px] leading-none text-[#14130f] mt-1">Saved pairings</h1>
              {(client || project) && <p className="mt-2 text-[11px] text-[#3b3933]">{[client && `Prepared for ${client}`, project].filter(Boolean).join(" · ")}</p>}
            </div>
            <LogoMark variant="dark" className="h-14 w-auto" />
          </header>
          {pairs.length === 0 && <p className="text-[#5d5a52]">No saved pairings yet.</p>}
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {pairs.map((p, n) => (
              <article key={pairKey(p)} className="print-card border border-[#e6dfcc] p-3" data-testid={`pair-print-${n}`}>
                <p className="font-mono-j text-[9px] tracking-[0.25em] text-[#9a7b26] mb-2">PAIRING {String(n + 1).padStart(2, "0")}</p>
                <div className="grid grid-cols-3 gap-2">
                  <Swatch label="Board" item={get(p.board)} />
                  <Swatch label="Stone" item={get(p.stone)} />
                  <Swatch label="Handle" item={get(p.handle)} extra={p.finish && (
                    <p className="mt-0.5 flex items-center gap-1 text-[9px] text-[#3b3933]"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: finishBg(p.finish) }} />{p.finish}</p>
                  )} />
                </div>
              </article>
            ))}
          </div>
          <p className="mt-6 text-[9px] text-[#8a857a]">Printed {new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</p>
        </section>
      </div>
    </div>
  );
}
